/*
 * Session allocation on session tasks: the list's Held by chip, and the form's
 * confirm before it claims a session someone else is joining.
 */

import type { Tone } from '../ui';
import { fmtTime } from './allocation';
import { zoneAbbrev } from '../local_time';

export const SESSION_TASK_TYPES = ['SESSION_JOIN_CLOSE', 'BACKUP_SESSION_JOIN'];

export interface AllocTaskItem {
  task_type?: string | null;
  claimed_by_ops_uuid?: string | null;
  student_name?: string | null;
  // join_at (end - 10 min) is requested from the list hydration; the chip drops the time without it.
  session_allocation?: {
    ops_uuid?: string | null;
    name?: string | null;
    join_at?: string | null;
  } | null;
}

export const isSessionTask = (item: AllocTaskItem): boolean =>
  SESSION_TASK_TYPES.includes(item.task_type || '');

const allocatedUuid = (item: AllocTaskItem): string => item.session_allocation?.ops_uuid || '';

const allocatedName = (item: AllocTaskItem): string =>
  item.session_allocation?.name || 'Another rep';

const JOIN_WINDOW_MS = 10 * 60 * 1000;

// "7:20 PM IST": the zone label says which clock the time is on.
const joinTime = (item: AllocTaskItem, tz?: string): string => {
  const at = item.session_allocation?.join_at;
  const zone = tz || 'Asia/Kolkata';
  const time = fmtTime(at, zone);
  if (!time) return '';
  const abbr = zoneAbbrev(zone, Date.parse(at || ''));
  return abbr ? `${time} ${abbr}` : time;
};

// The session ended once its join window closed; unknown (no join_at) counts as not ended.
const sessionEnded = (item: AllocTaskItem, now: number): boolean => {
  const join = Date.parse(item.session_allocation?.join_at || '');
  return !Number.isNaN(join) && join + JOIN_WINDOW_MS <= now;
};

// Open session rows only: "Nobody allocated" when no one is; nothing on closed or past sessions.
export const allocationChip = (
  item: AllocTaskItem,
  meUuid: string,
  tz?: string,
  opts: { terminal?: boolean; now?: number } = {}
): { label: string; tone: Tone } | null => {
  if (!isSessionTask(item) || opts.terminal) return null;
  if (sessionEnded(item, opts.now ?? Date.now())) return null;
  const uuid = allocatedUuid(item);
  if (!uuid) return { label: 'Nobody allocated', tone: 'error' };
  const at = joinTime(item, tz);
  const tail = at ? ` ${at}` : '';
  if (meUuid && uuid === meUuid) return { label: `You join${tail}`, tone: 'success' };
  return { label: `${allocatedName(item)} joins${tail}`, tone: 'warning' };
};

export interface AllocationConfirm {
  title: string;
  body: string;
  leave_label: string;
  confirm_label: string;
  leave_note: string;
}

// Opening claims an open task; ask first when the session is someone else's to join.
export const allocationClaimConfirm = (
  item: AllocTaskItem | null | undefined,
  meUuid: string,
  terminal: boolean,
  tz?: string
): AllocationConfirm | null => {
  if (!item || terminal || !isSessionTask(item)) return null;
  const uuid = allocatedUuid(item);
  if (!uuid || uuid === meUuid) return null;
  // Held by anyone (me, the allocated rep or a third rep): opening claims nothing new,
  // and the existing taken / not-holder flow speaks for itself.
  if (item.claimed_by_ops_uuid) return null;
  const name = allocatedName(item);
  const student = item.student_name || 'this student';
  const at = joinTime(item, tz);
  return {
    title: `${name} is joining this session`,
    body: `The team allocated ${student} to ${name}${at ? `, joining at ${at}` : ''}. Open and claim it only if ${name} can't make it. ${name} stays allocated and sees that you claimed it.`,
    leave_label: `Leave it for ${name}`,
    confirm_label: 'Open and claim',
    leave_note: `This task stays unclaimed for ${name}.`
  };
};

export type AllocDecision = 'accepted' | 'declined' | null;

// claim = run the existing claim; confirm = ask first; hold = declined, stay unclaimed and read-only;
// none = the form would not claim on open anyway.
export type FormClaimStep = 'claim' | 'confirm' | 'hold' | 'none';

export const formClaimStep = (
  form: AllocTaskItem | null | undefined,
  meUuid: string,
  claimable: boolean,
  decision: AllocDecision
): FormClaimStep => {
  if (!form || !claimable) return 'none';
  if (decision === 'accepted') return 'claim';
  if (!allocationClaimConfirm(form, meUuid, false)) return 'claim';
  return decision === 'declined' ? 'hold' : 'confirm';
};
